import { useState, useEffect, useCallback } from "react";
import { apiClient } from "@/services/apiClient";
import { EntityLink } from "./useEntityLinks";

export interface CalendarEvent {
  id: string;
  clientId: string;
  eventName: string;
  eventMonth: number | null;
  eventDay: number | null;
  endMonth: number | null;
  endDay: number | null;
  movable: boolean;
  dateYear: number | null;
  category: string;
  relevance: string;
  contentAngle: string;
  planningLeadDays: number;
  status: string;
  links: EntityLink[];
  generatedAt: string | null;
  dismissedAt: string | null;
}

interface CalendarResponse {
  events: CalendarEvent[];
}

interface UseCalendarOptions {
  clientId: string;
  status?: string;
}

export function useCalendar({ clientId, status }: UseCalendarOptions) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);

      const response = await apiClient.get<CalendarResponse>(
        `/api/clients/${clientId}/calendar?${params.toString()}`
      );
      setEvents(response.events || []);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load calendar"
      );
    } finally {
      setLoading(false);
    }
  }, [clientId, status]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const generate = useCallback(async () => {
    setGenerating(true);
    try {
      await apiClient.post<{ eventsCreated: number }>(
        `/api/clients/${clientId}/calendar/generate`,
        {}
      );
      await fetchEvents();
    } catch (err) {
      throw err instanceof Error
        ? err
        : new Error("Failed to generate calendar");
    } finally {
      setGenerating(false);
    }
  }, [clientId, fetchEvents]);

  const createEvent = useCallback(
    async (data: {
      event_name: string;
      event_month?: number | null;
      event_day?: number | null;
      end_month?: number | null;
      end_day?: number | null;
      movable?: boolean;
      date_year?: number | null;
      category?: string;
      relevance?: string;
      content_angle?: string;
      planning_lead_days?: number;
    }) => {
      const response = await apiClient.post<{ event: CalendarEvent }>(
        `/api/clients/${clientId}/calendar`,
        data
      );
      setEvents((prev) => [...prev, response.event]);
      return response.event;
    },
    [clientId]
  );

  const updateEvent = useCallback(
    async (
      eventId: string,
      data: {
        status?: string;
        event_month?: number | null;
        event_day?: number | null;
        end_month?: number | null;
        end_day?: number | null;
        movable?: boolean;
        date_year?: number | null;
        event_name?: string;
        content_angle?: string;
        relevance?: string;
        category?: string;
        planning_lead_days?: number;
      }
    ) => {
      try {
        const response = await apiClient.patch<{ event: CalendarEvent }>(
          `/api/calendar/${eventId}`,
          data
        );
        setEvents((prev) =>
          prev.map((e) =>
            e.id === eventId ? { ...e, ...response.event } : e
          )
        );
      } catch (err) {
        throw err instanceof Error
          ? err
          : new Error("Failed to update event");
      }
    },
    []
  );

  const deleteEvent = useCallback(
    async (eventId: string) => {
      try {
        await apiClient.delete(`/api/calendar/${eventId}`);
        setEvents((prev) => prev.filter((e) => e.id !== eventId));
      } catch (err) {
        throw err instanceof Error
          ? err
          : new Error("Failed to delete event");
      }
    },
    []
  );

  return {
    events,
    loading,
    error,
    generating,
    refresh: fetchEvents,
    generate,
    createEvent,
    updateEvent,
    deleteEvent,
  };
}
